/**
 * Service für die Behandlung von Portal-Intent-Aktionen
 *
 * Verwaltet Kommunikation mit dem Portal über Intents, einschließlich
 * Registrierung von Callbacks für eingehende Intents und Aussenden von Intents
 * an andere Anwendungen in der Portal-Umgebung.
 *
 * @class IntentActionService
 */
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NGXLogger } from 'ngx-logger';
import { from } from 'rxjs';
import { PortalMessageBroker } from '../portal/portal-message-broker';

@Injectable({
  providedIn: 'root',
})
export class IntentActionService {
  private _logger = inject(NGXLogger);
  private _portalMessageBroker = inject(PortalMessageBroker);
  private _destroyRef = inject(DestroyRef);

  /**
   * Konstruktor initialisiert Intent-Callbacks wenn im Portal ausgeführt
   *
   * Signalisiert Abschluss der Registrierung an den Portal-Message-Broker.
   * Intent-Handler können hier registriert werden wenn benötigt.
   */
  constructor() {
    if (this.isRunningInPortal()) {
      // Intent-Handler hier registrieren wenn benötigt:
      // this._portalMessageBroker.registerIntentCallback(intentId, version, handler);

      this._portalMessageBroker.allIntentCallbackRegistered();
    }
  }

  /**
   * Prüft ob die Anwendung innerhalb des Portals läuft
   *
   * @returns True wenn im Portal läuft, false andernfalls
   */
  public isRunningInPortal(): boolean {
    return this._portalMessageBroker.isRunningInPortal();
  }

  /**
   * Sendet einen Intent an das Portal aus
   *
   * Konvertiert Promise zu Observable und behandelt Erfolgs-/Fehlerfälle.
   *
   * @param intentId - Die Intent-Kennung
   * @param intentVersion - Die Intent-Version
   * @param data - Die Intent-Payload-Daten
   * @throws Error wenn App nicht im Portal läuft
   */
  protected doEmit<T>(intentId: string, intentVersion: string, data: T): void {
    if (!this.isRunningInPortal()) {
      throw new Error('app is not running in portal');
    }

    const promise = this._portalMessageBroker.emitIntent(intentId, intentVersion, data);
    from(promise)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (value) => {
          this._logger.debug(
            'IntentActionService.doEmit(): id=' + intentId + ' version=' + intentVersion + ' handles successful',
            value,
          );
        },
        error: (error) => {
          this._logger.error(
            'IntentActionService.doEmit(): id=' + intentId + ' version=' + intentVersion + ' dispatch failed',
            error,
          );
        },
      });
  }
}
